'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';
import { CardBody } from './styleConstants';
import styles from './FeaturesSection.module.css';

/* ── Data ──────────────────────────────────────────────── */

type FeatureData = {
  id: string;
  key: string;
  imageSrc: string;
};

const FEATURES_DATA: FeatureData[] = [
  {
    id: 'sleep-index',
    key: 'sleepIndex',
    imageSrc: 'web_v2/ring-pro/landing-page/features/sleep.png',
  },
  {
    id: 'dynamic-recovery',
    key: 'dynamicRecovery',
    imageSrc: 'web_v2/ring-pro/landing-page/features/recovery.png',
  },
  {
    id: 'stress-rhythm',
    key: 'stressRhythm',
    imageSrc: 'web_v2/ring-pro/landing-page/features/stress-bnw.png',
  },
  {
    id: 'movement-index',
    key: 'movementIndex',
    imageSrc: 'web_v2/ring-pro/landing-page/features/movement.png',
  },
  {
    id: 'ultra-age',
    key: 'ultraAge',
    imageSrc: 'web_v2/ring-pro/landing-page/features/ultra-age-new.png',
  },
  {
    id: 'powerplugs',
    key: 'powerPlugs',
    imageSrc: 'web_v2/ring-pro/landing-page/features/powerplugs.png',
  },
  {
    id: 'womens-health',
    key: 'womensHealth',
    imageSrc: 'web_v2/ring-pro/landing-page/features/womens-health-pro.png',
  },
  // DISABLED: Respiratory Health PowerPlug feature card — re-enable by uncommenting
  // {
  //   id: 'respiratory-health',
  //   key: 'respiratoryHealth',
  //   imageSrc: 'web_v2/ring-pro/landing-page/features/respiratory-health.png',
  // },
  {
    id: 'heart-health',
    key: 'heartHealth',
    imageSrc: 'web_v2/ring-pro/landing-page/features/heart-health.png',
  },
];

const WOMENS_HEALTH_IMAGE = {
  pro: 'web_v2/ring-pro/landing-page/features/womens-health-pro.png',
  plus: 'web_v2/ring-pro/landing-page/features/womens-health-plus.png',
} as const;

/* ── Component ─────────────────────────────────────────── */

const FeaturesSection = () => {
  const t = useTranslations('ring-pro');
  const { getValue } = useIsCnOPlus();

  const features = useMemo(
    () =>
      FEATURES_DATA.map((feature) =>
        feature.id === 'womens-health'
          ? {
              ...feature,
              imageSrc: getValue(WOMENS_HEALTH_IMAGE.pro, WOMENS_HEALTH_IMAGE.plus),
            }
          : feature,
      ),
    [getValue],
  );

  return (
    <section className={styles.section}>
      <div className={styles.contentContainer}>
        <h2 className={styles.heading}>{t('ring-pro.features.heading')}</h2>
        <div className={styles.featuresGrid}>
          {features.map((feature) => (
            <div className={styles.featureCard} key={feature.id}>
              <CustomImage
                className={styles.featureImage}
                src={feature.imageSrc}
                alt={t(`ring-pro.features.${feature.key}.title`)}
                width={398}
                height={398}
              />
              <div className={styles.textContainer}>
                <p className={styles.cardTitle}>{t(`ring-pro.features.${feature.key}.title`)}</p>
                <CardBody>{t(`ring-pro.features.${feature.key}.description`)}</CardBody>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
